import { useCallback, useMemo, useRef, useState } from "react";
import type { z, ZodError, ZodTypeAny } from "zod";

export type FieldErrors<T> = Partial<Record<keyof T, string>>;

export type UseZodFormResult<T> = {
  values: T;
  errors: FieldErrors<T>;
  touched: Partial<Record<keyof T, boolean>>;
  dirty: boolean;
  submitting: boolean;
  formError: string | null;
  setField: <K extends keyof T>(key: K, value: T[K]) => void;
  patch: (next: Partial<T>) => void;
  reset: (next?: Partial<T>) => void;
  resetAfterSave: (next?: T) => void;
  validate: () =>
    | { ok: true; data: T }
    | { ok: false; errors: FieldErrors<T> };
  /**
   * Validate one field and set/clear just its error. Wire to a
   * field's `onBlur` for professional live feedback — the input
   * turns red the moment focus leaves it with a bad value.
   */
  validateField: <K extends keyof T>(key: K) => void;
  markTouched: <K extends keyof T>(key: K) => void;
  clearError: <K extends keyof T>(key: K) => void;
  setServerErrors: (errors: FieldErrors<T>) => void;
  setSubmitting: (submitting: boolean) => void;
  setFormError: (error: string | null) => void;
  registerField: <K extends keyof T>(key: K) => (node: HTMLElement | null) => void;
  focusFirstInvalid: (errors?: FieldErrors<T>) => void;
  shouldBlockNavigation: boolean;
};

export function useZodForm<TSchema extends ZodTypeAny>(
  schema: TSchema,
  initial: z.input<TSchema>,
): UseZodFormResult<z.input<TSchema>> {
  type T = z.input<TSchema>;
  const baseline = useMemo(() => initial, [initial]);
  const baselineRef = useRef<T>(baseline);
  const fieldRefs = useRef(new Map<keyof T, HTMLElement>());
  const [values, setValues] = useState<T>(baseline);
  const [errors, setErrors] = useState<FieldErrors<T>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const dirty = !structurallyEqual(values, baselineRef.current);

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setFormError(null);
    setErrors((current) => {
      if (!(key in current)) return current;
      const { [key]: _drop, ...rest } = current;
      return rest as FieldErrors<T>;
    });
  }, []);

  const patch = useCallback((next: Partial<T>) => {
    setValues((current) => ({ ...current, ...next }));
    setFormError(null);
  }, []);

  const reset = useCallback(
    (next?: Partial<T>) => {
      setValues({ ...baseline, ...(next ?? {}) });
      setErrors({});
      setTouched({});
      setFormError(null);
    },
    [baseline],
  );

  const resetAfterSave = useCallback((next?: T) => {
    const committed = next ?? values;
    baselineRef.current = committed;
    setValues(committed);
    setErrors({});
    setTouched({});
    setFormError(null);
  }, [values]);

  const focusFirstInvalid = useCallback((nextErrors: FieldErrors<T> = errors) => {
    for (const key of Object.keys(nextErrors) as (keyof T)[]) {
      const node = fieldRefs.current.get(key);
      if (!node) continue;
      node.focus();
      node.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
  }, [errors]);

  const validate = useCallback(() => {
    const result = schema.safeParse(values);
    if (result.success) {
      setErrors({});
      return { ok: true as const, data: result.data as T };
    }
    const fieldErrors = zodErrorToFieldMap<T>(result.error);
    setErrors(fieldErrors);
    setTouched(
      Object.fromEntries(Object.keys(fieldErrors).map((key) => [key, true])) as Partial<
        Record<keyof T, boolean>
      >,
    );
    queueMicrotask(() => focusFirstInvalid(fieldErrors));
    return { ok: false as const, errors: fieldErrors };
  }, [focusFirstInvalid, schema, values]);

  const validateField = useCallback(
    <K extends keyof T>(key: K) => {
      const result = schema.safeParse(values);
      const message = result.success
        ? undefined
        : zodErrorToFieldMap<T>(result.error)[key];
      setTouched((current) => ({ ...current, [key]: true }));
      setErrors((current) => {
        if (message) return { ...current, [key]: message };
        if (!(key in current)) return current;
        const { [key]: _drop, ...rest } = current;
        return rest as FieldErrors<T>;
      });
    },
    [schema, values],
  );

  const clearError = useCallback(<K extends keyof T>(key: K) => {
    setErrors((current) => {
      if (!(key in current)) return current;
      const { [key]: _drop, ...rest } = current;
      return rest as FieldErrors<T>;
    });
  }, []);

  const markTouched = useCallback(<K extends keyof T>(key: K) => {
    setTouched((current) => ({ ...current, [key]: true }));
  }, []);

  const setServerErrors = useCallback((next: FieldErrors<T>) => {
    setErrors(next);
    setTouched(
      Object.fromEntries(Object.keys(next).map((key) => [key, true])) as Partial<
        Record<keyof T, boolean>
      >,
    );
    queueMicrotask(() => focusFirstInvalid(next));
  }, [focusFirstInvalid]);

  const registerField = useCallback(
    <K extends keyof T>(key: K) => (node: HTMLElement | null) => {
      if (node) fieldRefs.current.set(key, node);
      else fieldRefs.current.delete(key);
    },
    [],
  );

  return {
    values,
    errors,
    touched,
    dirty,
    submitting,
    formError,
    setField,
    patch,
    reset,
    resetAfterSave,
    validate,
    validateField,
    markTouched,
    clearError,
    setServerErrors,
    setSubmitting,
    setFormError,
    registerField,
    focusFirstInvalid,
    shouldBlockNavigation: dirty && !submitting,
  };
}

function zodErrorToFieldMap<T>(error: ZodError): FieldErrors<T> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const head = issue.path[0];
    if (typeof head === "string" && !(head in out)) {
      out[head] = issue.message;
    }
  }
  return out as FieldErrors<T>;
}

function structurallyEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (typeof left !== typeof right || left === null || right === null) return false;
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.every((value, index) => structurallyEqual(value, right[index]));
  }
  if (typeof left !== "object" || typeof right !== "object") return false;
  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const leftKeys = Object.keys(leftRecord);
  const rightKeys = Object.keys(rightRecord);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(rightRecord, key) && structurallyEqual(leftRecord[key], rightRecord[key]),
  );
}
